import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useAuthStore } from "@/store/authStore";

/**
 * Phase 1 Invitations API (backend.md §24.4).
 *
 * Home-client and cross-client invites share the `cross_client_invitations`
 * table on the backend — one list endpoint, two write paths (home invites
 * happen via POST /users/invite; cross-client via POST /invitations).
 *
 * Tenant-scoped queryKeys carry `actingAs.clientId` (CROSS_PAGE_DECISIONS
 * §13) so the cache partitions per impersonation context. The public
 * by-token endpoint and the email-precheck endpoint are not
 * tenant-scoped (the latter resolves to the caller's home/acting-as
 * tenant server-side, but the cache key still tracks impersonation so
 * a switch invalidates).
 */

export type InvitationStatus =
  | "pending"
  | "accepted"
  | "rejected"
  | "revoked"
  | "expired";

/**
 * Slice 6 (MV2-901) — the grant an accepted invitation produced, embedded
 * on the list AND the detail. The invitation's own `status` stays
 * `accepted` after a grant revoke, so without this the list cannot tell a
 * live grant from a revoked or ended one.
 */
export interface InvitationGrantSummary {
  id: string;
  access_start_date: string;
  access_end_date: string | null;
  active: boolean;
  revoked_at: string | null;
  revoked_by: string | null;
  revoked_by_name: string | null;
  revoke_reason: string | null;
}

/**
 * One row of `GET /invitations/{id}/events` — the first reader of
 * `cross_client_access_events` (backend.md §53.4). Oldest first.
 *
 * `action` vocabulary, pinned by a backend drift-guard test:
 * `invited` · `invitation_edited` · `accepted` · `rejected` · `revoked_grant`.
 * Revoking a *pending* invitation and resend / renew write only `audit_log`,
 * no access event — the timeline draws those two steps from the row's own
 * `revoked_at` / `last_email_sent_at` (see `crossClientState.ts`).
 */
export type CrossClientAccessAction =
  | "invited"
  | "invitation_edited"
  | "accepted"
  | "rejected"
  | "revoked_grant";

export interface CrossClientAccessEvent {
  id: string;
  invitation_id: string | null;
  grant_id: string | null;
  action: CrossClientAccessAction | string;
  performed_at: string;
  performed_by: string | null;
  performed_by_name: string | null;
  description: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
}

export interface InvitationResponse {
  id: string;
  inviting_client_id: string;
  invited_user_id: string;
  invited_email_snapshot: string;
  invited_user_name_snapshot: string;
  site_ids: string[];
  policy_ids: string[];
  status: InvitationStatus;
  message: string | null;
  /** When the invitation *token* expires (invitee can no longer accept). */
  expires_at: string | null;
  /**
   * MV2-243: "access valid until" — when the granted access window
   * ends. Propagates to ``cross_client_grants.access_end_date`` on
   * accept. ``null`` means open-ended access.
   */
  access_end_date: string | null;
  created_at: string;
  created_by: string;
  accepted_at: string | null;
  rejected_at: string | null;
  revoked_at: string | null;
  last_email_sent_at: string | null;
  /**
   * MV2-869 — the invitee's view: who offers what, by name (from
   * `/me/cross-client-invitations`). Slice 6 (MV2-901): the admin list and
   * detail hydrate these too, in six queries per page regardless of rows.
   */
  inviting_client_name?: string | null;
  invited_by_name?: string | null;
  site_names?: string[];
  policy_names?: string[];
  /** Slice 6 — the invitee's home client, the "from" half of "From → to". */
  invitee_home_client_id?: string | null;
  invitee_home_client_name?: string | null;
  /** Slice 6 — who granted it ("Granted by" on the row). Same value as `invited_by_name`. */
  created_by_name?: string | null;
  /** Slice 6 — the grant an accepted invitation produced; `null` until accepted. */
  grant?: InvitationGrantSummary | null;
}

export interface InvitationByToken {
  invited_email_snapshot: string;
  invited_user_name_snapshot: string;
  inviting_client_name: string;
  status: InvitationStatus;
  expires_at: string | null;
  /**
   * MV2-205: ``home`` invitations land on a brand-new user row and need
   * the password-setup flow; ``cross_client`` invitations target an
   * existing Cognito user and just need a sign-in + review-and-accept.
   */
  invitation_type: "home" | "cross_client";
  /** Cross-client only — needed to call POST /invitations/{id}/accept. */
  invitation_id: string | null;
}

export interface CheckEmailResponse {
  user_exists: boolean;
  pending_invitation_id: string | null;
}

export interface CrossClientInviteCreate {
  email: string;
  site_ids: string[];
  policy_ids?: string[];
  /** Invitation *token* TTL — when the invitee can no longer accept. */
  expires_at?: string | null;
  /**
   * MV2-243: "access valid until" — propagates to the grant's
   * ``access_end_date`` on accept. ``null`` = open-ended.
   */
  access_end_date?: string | null;
  message?: string | null;
}

export type InvitationKind = "cross_client";

/**
 * `GET /invitations`. Slice 6 adds `kind=cross_client` — the server keeps
 * only rows with `cardinality(site_ids) > 0`, replacing the client-side
 * `site_ids.length > 0` filter the old cross-client page did.
 */
export function useInvitations(
  params: { status?: InvitationStatus; kind?: InvitationKind } = {},
) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<InvitationResponse[]>({
    queryKey: [
      "invitations",
      params.status ?? null,
      params.kind ?? null,
      actingAsClientId,
    ],
    queryFn: async () =>
      (
        await apiClient.get<InvitationResponse[]>("/invitations", {
          params: {
            ...(params.status ? { status_filter: params.status } : {}),
            ...(params.kind ? { kind: params.kind } : {}),
          },
        })
      ).data,
    staleTime: 15_000,
  });
}

/**
 * Slice 6 (MV2-901) — `GET /invitations/{id}/events`, oldest first. The
 * timeline's spine on `/admin/cross-client/:id`. A 404 (backend predates
 * PR #494) is not retried; the timeline then draws only the steps it can
 * derive from the invitation row's own timestamps.
 */
export function useInvitationEvents(invitationId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<CrossClientAccessEvent[]>({
    queryKey: ["invitation-events", invitationId, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<CrossClientAccessEvent[]>(
          `/invitations/${invitationId}/events`,
        )
      ).data,
    enabled: Boolean(invitationId),
    retry: false,
    staleTime: 15_000,
  });
}

export function useInvitation(invitationId: string | undefined) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  return useQuery<InvitationResponse>({
    queryKey: ["invitation", invitationId, actingAsClientId],
    queryFn: async () =>
      (await apiClient.get<InvitationResponse>(`/invitations/${invitationId}`))
        .data,
    enabled: Boolean(invitationId),
  });
}

/**
 * Public unauthenticated endpoint — powers the /accept-invite landing
 * page. apiClient's bearer interceptor is still attached but the backend
 * doesn't require a token here; we short-circuit it by using a fresh
 * fetch so an anonymous user never hits a 401.
 */
export function useInvitationByToken(token: string | undefined) {
  return useQuery<InvitationByToken>({
    queryKey: ["invitation-by-token", token],
    queryFn: async () => {
      if (!token) throw new Error("missing token");
      const base = apiClient.defaults.baseURL ?? "";
      const res = await fetch(
        `${base}/invitations/by-token/${encodeURIComponent(token)}`,
      );
      if (!res.ok) {
        // MV2-909: carry the status and the envelope so the page can tell a
        // 404 (not a valid link) from a failed lookup (retry), and quote the
        // request id — `apiErrorStatus` / `apiRequestId` read this shape.
        const data = await res.json().catch(() => null);
        throw Object.assign(
          new Error(`invitation lookup failed: ${res.status}`),
          {
            response: { status: res.status, data },
          },
        );
      }
      return (await res.json()) as InvitationByToken;
    },
    enabled: Boolean(token),
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * Form-assist helper — the invite form calls this on email blur to surface
 * "user already exists" / "pending invite exists" hints before submit.
 *
 * MV2-350: email is normalised at the hook boundary so the queryKey,
 * the request param, and the eventual invite submission all reference
 * the canonical lowercase form. Backend's lookup is now
 * case-insensitive (`func.lower(User.email) == :email`) but we
 * normalise client-side too for cache stability + clean network logs.
 */
export function useCheckInviteEmail(email: string | null) {
  const actingAsClientId = useAuthStore((s) => s.actingAs?.clientId ?? null);
  const normalized = email ? email.trim().toLowerCase() : null;
  return useQuery<CheckEmailResponse>({
    queryKey: ["invitations", "check-email", normalized, actingAsClientId],
    queryFn: async () =>
      (
        await apiClient.get<CheckEmailResponse>("/invitations/check-email", {
          params: { email: normalized },
        })
      ).data,
    enabled: Boolean(
      normalized && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized),
    ),
    staleTime: 10_000,
  });
}

export function useRenewInvitation() {
  const qc = useQueryClient();
  return useMutation<InvitationResponse, Error, string>({
    mutationFn: async (invitationId) =>
      (
        await apiClient.post<InvitationResponse>(
          `/invitations/${invitationId}/renew`,
        )
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
      qc.invalidateQueries({ queryKey: ["invitation", data.id] });
      qc.invalidateQueries({ queryKey: ["invitation-events", data.id] });
    },
  });
}

/**
 * MV2-240: edit a pending cross-client invitation. All fields are
 * optional — only supply what's changing. The backend auto-resends the
 * email after a successful PATCH so the invitee sees the new scope
 * before accepting.
 */
export interface CrossClientInvitePatch {
  site_ids?: string[];
  policy_ids?: string[];
  expires_at?: string | null;
  /**
   * MV2-243: "access valid until" — null clears the field
   * (open-ended); a date in the future updates the grant's window on
   * (re-)accept.
   */
  access_end_date?: string | null;
  message?: string | null;
}

export function useEditCrossClientInvitation() {
  const qc = useQueryClient();
  return useMutation<
    InvitationResponse,
    Error,
    { invitationId: string; patch: CrossClientInvitePatch }
  >({
    mutationFn: async ({ invitationId, patch }) =>
      (
        await apiClient.patch<InvitationResponse>(
          `/invitations/${invitationId}`,
          patch,
        )
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
      qc.invalidateQueries({ queryKey: ["invitation", data.id] });
      qc.invalidateQueries({ queryKey: ["invitation-events", data.id] });
    },
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation<
    InvitationResponse,
    Error,
    { invitationId: string; reason?: string }
  >({
    mutationFn: async ({ invitationId, reason }) =>
      (
        await apiClient.post<InvitationResponse>(
          `/invitations/${invitationId}/revoke`,
          {
            reason: reason ?? null,
          },
        )
      ).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
      qc.invalidateQueries({ queryKey: ["invitation", data.id] });
      qc.invalidateQueries({ queryKey: ["invitation-events", data.id] });
    },
  });
}

/**
 * Cross-client invitation create — POST /invitations. Requires feature
 * `invite_cross_client`; backend enforces no-escalation-beyond-grantor.
 */
export function useCreateCrossClientInvitation() {
  const qc = useQueryClient();
  return useMutation<InvitationResponse, Error, CrossClientInviteCreate>({
    mutationFn: async (body) =>
      (await apiClient.post<InvitationResponse>("/invitations", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
    },
  });
}

/**
 * Revoke a cross-client grant tied to an invitation. Inviting-client
 * only — §9.5.
 */
export function useRevokeGrant() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { invitationId: string; reason?: string }>(
    {
      mutationFn: async ({ invitationId, reason }) =>
        (
          await apiClient.post(`/invitations/${invitationId}/revoke-grant`, {
            reason: reason ?? null,
          })
        ).data,
      onSuccess: (_data, { invitationId }) => {
        qc.invalidateQueries({ queryKey: ["invitations"] });
        qc.invalidateQueries({ queryKey: ["invitation", invitationId] });
        qc.invalidateQueries({ queryKey: ["invitation-events", invitationId] });
        qc.invalidateQueries({ queryKey: ["my-invitations"] });
      },
    },
  );
}
